import { type GeoPoint } from 'utils/geohash';
import { type BlockedRoad } from 'api/dispatch-monitor.api';

// A blocked-road entry as tracked client-side: the server payload plus a
// locally-stamped absolute deadline (Date.now() + ttlSeconds at the moment
// this entry was fetched). `ttlSeconds` from the API is "remaining as of
// this response" — recomputing a deadline from it on every later poll
// would drift, so callers that need to know exactly when a road unblocks
// (the 1s local expiry sweep, the hover countdown) should use `expiresAt`,
// not `ttlSeconds`. `null` means blocked indefinitely.
export interface BlockedRoadEntry extends BlockedRoad {
  expiresAt: number | null;
}

// Zone colors per location type (loading/dumping match DispatchPlanMap)
export const LOCATION_TYPE_COLORS: Record<string, string> = {
  loading: '#4ade80',
  dumping: '#fbbf24',
  parking: '#a78bfa',
  workshop: '#94a3b8',
  fueling: '#fb923c',
  weighbridge: '#2dd4bf',
  crushing: '#f87171',
  crusher: '#f87171',
  processing: '#22d3ee',
  stockpile: '#e879f9',
  intersection: '#60a5fa',
};
export const ROUTE_COLOR = '#2c67b9';
export const INACTIVE_ROUTE_COLOR = '#94a3b8';
export const PENDING_ROUTE_COLOR = '#f59e0b';
export const BLOCKED_ROUTE_COLOR = '#ef4444';
export const GRADE_ZONE_COLOR = '#c026d3';
// Highlight color for the route currently selected in the Find Route panel
// — deliberately distinct from ROUTE_COLOR/PENDING_ROUTE_COLOR/
// BLOCKED_ROUTE_COLOR so a found route stands out over any of those states.
export const FOUND_ROUTE_COLOR = '#22c55e';
// A user-imported GeoJSON file's line/fill/point color — deliberately
// distinct from every other layer color above so it stands out over roads,
// zones, and routes.
export const IMPORTED_GEOJSON_COLOR = '#0ea5e9';

// Grade zone colors per material category, keyed lowercase for lookup
export const GRADE_CATEGORY_COLORS: Record<string, string> = {
  waste: '#1e1717',
  'mineralized waste': '#c2b280',
  oxide: '#d1c12e',
  'oxide high grade': '#fd8d3c',
  'oxide low grade': '#fecc5c',
  'offbench oxide': '#999999',
  secondary: '#1f78b4',
  'secondary low grade': '#a6cee3',
  'offbench secondary': '#b2df8a',
  other: '#c026d3',
};
export const DEFAULT_GEOHASH_PRECISION = 9;

// Mine map GeoJSON overlay + plain-basemap default + layer picker are
// specific to this customer's site — other customers keep today's behavior.
export const CAR_CUSTOMER_CODE = 'car';
export const ALMALY_ROADS_GEOJSON_URL = '/geojson/almaly_roads.geojson';
export const ALMALY_ROADS_COLOR = '#9f9c99';

// Fallback local-grid origin for almaly_roads.geojson, used only when the
// `car` customer record has no localOriginEasting/localOriginNorthing of its
// own (as of writing, it doesn't — see CAR_CUSTOMER_CODE usage). Without an
// origin, the DXF's raw local coordinates (tens of thousands, not the
// millions-scale absolute Gauss-Krüger values the proj4String expects)
// reproject to nowhere near the actual site.
//
// Derived, not surveyed: almaly_roads.geojson has no CRS/origin metadata of
// its own, so this was back-solved by matching its local-grid bounding box
// against the already-WGS84 /geojson/my-layer.geojson (the other car-customer
// road asset, covering the same site) through this same proj4String. The two
// bounding boxes agree to within ~1m across the ~7km site once this origin is
// applied — see the derivation in git history for this comment if it ever
// needs re-checking. Drop this fallback once the customer record's real
// origin is populated (that will always take precedence — see
// useMineMapOverlay.ts).
export const ALMALY_FALLBACK_LOCAL_ORIGIN_EASTING = 13399902.434051637;
export const ALMALY_FALLBACK_LOCAL_ORIGIN_NORTHING = 5299996.891340249;

export const DRAG_HIT_PX = 20;

// The "Move Centroid" pin billboard's rendered size in pixels (see
// locationPinIconDataUri) — used both to size the SVG and, since the
// billboard is bottom/center-anchored to its geo point (the pin's tip
// touches the ground, the icon extends upward from there), to hit-test a
// grab against the actual rectangle it's drawn in rather than a small
// circle around that anchor point.
export const CENTROID_PIN_ICON_PX = 48;

// All roads render at a fixed visual width on the map, regardless of their
// stored per-segment width data (used for distance/capacity calculations,
// not display) — actual road width varies too little at mine-site scale to
// be worth distinguishing visually, and a uniform width reads more cleanly.
export const ROAD_DISPLAY_WIDTH_METERS = 3;

// Corridor width is real-world meters, so a fixed width shrinks below a
// visible pixel size and disappears once the camera is far enough away.
// Past that point, corridor width scales with camera height instead
// (see zoomAwareRoadWidth) — this is the meters-of-width added per meter
// of camera height, tuned so roads stay visibly on-screen at any zoom.
export const ROAD_WIDTH_ZOOM_FACTOR = 0.0045;

// Road drawing "reached the destination" hit-test is centroid-proximity
// based, not point-in-polygon — location zones can sit close to or overlap
// each other, so testing against the whole boundary risks landing the road
// on the wrong neighbor. A click has to land within this many meters of a
// location's centroid to count as reaching it.
export const ROAD_DESTINATION_HIT_RADIUS_METERS = 15;

export const ROAD_CONDITIONS = ['excellent', 'good', 'bad', 'very_bad'];

// Same options as AddLocationModal
export const LOCATION_TYPES = [
  'Loading',
  'Dumping',
  'Parking',
  'Workshop',
  'Fueling',
  'Weighbridge',
  'Crushing',
  'Processing',
  'Stockpile',
  'Intersection',
  'Other',
];

export interface NewLocationDraft {
  name: string;
  locationType: string;
  trucksCapacity: string;
  elevation: string;
  minutes: string;
}

export const EMPTY_NEW_LOCATION: NewLocationDraft = {
  name: '',
  locationType: LOCATION_TYPES[0],
  trucksCapacity: '',
  elevation: '387',
  minutes: '2',
};

// A road that's been drawn and finished but not yet POSTed to the API.
// widths[i] is the road width of the segment ending at path[i].
export interface PendingRoute {
  key: string;
  path: GeoPoint[];
  widths: number[];
  precision: number;
  fromLocationName: string;
  toLocationName: string;
  roadName: string;
  minutes: number;
  truckCapacity: number;
  weightCapacityInTon: number | null;
  speedLimitMinKph: number;
  speedLimitMaxKph: number;
  numberOfLanes: number;
  roadCondition: string;
  geoEngineerSuggestions: string;
}
